import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Site-wide final CTA band rendered once per page just above the global Footer.
 *
 * Suppresses itself on routes where a final marketing CTA would be inappropriate
 * (legal pages, auth, admin, portal, settings, dashboards, etc.).
 *
 * A/B testing
 * -----------
 * Variants live in `CTA_VARIANTS` below. The active variant is chosen by:
 *   1. `?cta=A|B|C` URL override (for QA / paid traffic targeting)
 *   2. `localStorage.vgp_cta_variant` (sticky per-visitor)
 *   3. The `variant` prop (manually pinned by a parent page)
 *   4. A weighted random pick across enabled variants
 *
 * To add a variant: drop a new entry in CTA_VARIANTS with a non-zero `weight`.
 * To kill a variant: set its weight to 0 (existing assignments will fall through
 * to the next allowed variant on next visit).
 *
 * Conversion is attributed by the `cta_variant` column on `gateway_events` —
 * a `cta_band_view` row is inserted on first paint.
 */

type CtaVariantId = "A" | "B" | "C";

interface CtaVariant {
  id: CtaVariantId;
  weight: number; // 0 = disabled
  eyebrow: string;
  headline: React.ReactNode;
  subheadline: string;
  primaryLabel: string;
  primaryHref: string;
}

const CTA_VARIANTS: Record<CtaVariantId, CtaVariant> = {
  A: {
    id: "A",
    weight: 50,
    eyebrow: "Your Next Step",
    headline: (
      <>
        Ready to install your{" "}
        <span className="bg-gradient-to-r from-secondary to-[hsl(38_55%_58%)] bg-clip-text text-transparent">
          revenue engine?
        </span>
      </>
    ),
    subheadline:
      "One 30-minute call. We pressure-test your acquisition stack and map a 90-day pipeline plan you can keep — partner or not.",
    primaryLabel: "Book a Call",
    primaryHref: "/apply#lead-form",
  },
  B: {
    id: "B",
    weight: 50,
    eyebrow: "Free Strategy Audit",
    headline: (
      <>
        Want a 90-day{" "}
        <span className="bg-gradient-to-r from-secondary to-[hsl(38_55%_58%)] bg-clip-text text-transparent">
          pipeline plan, free?
        </span>
      </>
    ),
    subheadline:
      "Tell us your goal in 60 seconds. We'll send back a written growth plan tailored to your business — no call required.",
    primaryLabel: "Get My Free Plan",
    primaryHref: "/apply#lead-form",
  },
  C: {
    id: "C",
    weight: 0, // disabled by default — flip to e.g. 25 to start exposing
    eyebrow: "Limited Slots This Month",
    headline: (
      <>
        Let's build your{" "}
        <span className="bg-gradient-to-r from-secondary to-[hsl(38_55%_58%)] bg-clip-text text-transparent">
          next £100k in pipeline
        </span>
      </>
    ),
    subheadline:
      "We onboard 4 new clients per month. Apply today to lock in our Q-end install slots and start generating qualified meetings within 14 days.",
    primaryLabel: "Apply for a Slot",
    primaryHref: "/apply#lead-form",
  },
};

const SUPPRESS_PREFIXES = [
  "/auth",
  "/settings",
  "/admin",
  "/dashboard",
  "/client-portal",
  "/portal",
  "/privacy-policy",
  "/terms-of-service",
  "/content-policy",
  "/unsubscribe",
  "/og-preview",
  "/app",
];

const STORAGE_KEY = "vgp_cta_variant";

/** Pick a variant id using weighted random across enabled variants. */
const weightedPick = (): CtaVariantId => {
  const enabled = Object.values(CTA_VARIANTS).filter((v) => v.weight > 0);
  if (enabled.length === 0) return "A";
  const total = enabled.reduce((sum, v) => sum + v.weight, 0);
  let r = Math.random() * total;
  for (const v of enabled) {
    r -= v.weight;
    if (r <= 0) return v.id;
  }
  return enabled[0].id;
};

/** Resolve which variant to render — URL override > prop > sticky storage > random. */
const resolveVariant = (forced?: CtaVariantId): CtaVariantId => {
  if (typeof window === "undefined") return forced ?? "A";

  // 1. URL override (?cta=B) — also persist so it sticks for the session
  const urlVariant = new URLSearchParams(window.location.search)
    .get("cta")
    ?.toUpperCase() as CtaVariantId | undefined;
  if (urlVariant && CTA_VARIANTS[urlVariant]) {
    try {
      localStorage.setItem(STORAGE_KEY, urlVariant);
    } catch {
      /* ignore */
    }
    return urlVariant;
  }

  // 2. Explicit prop pin
  if (forced && CTA_VARIANTS[forced]) return forced;

  // 3. Sticky per-visitor assignment
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as CtaVariantId | null;
    if (stored && CTA_VARIANTS[stored] && CTA_VARIANTS[stored].weight > 0) {
      return stored;
    }
  } catch {
    /* ignore */
  }

  // 4. Fresh weighted pick
  const picked = weightedPick();
  try {
    localStorage.setItem(STORAGE_KEY, picked);
  } catch {
    /* ignore */
  }
  return picked;
};

interface FinalCtaBandProps {
  /** Force a specific variant on this page (overrides random assignment). */
  variant?: CtaVariantId;
}

const FinalCtaBand = ({ variant: forcedVariant }: FinalCtaBandProps = {}) => {
  const { pathname } = useLocation();
  const variantId = useMemo(() => resolveVariant(forcedVariant), [forcedVariant]);
  const variant = CTA_VARIANTS[variantId];
  const hasTrackedImpression = useRef(false);

  // Fire one impression event per page so we can compute per-variant CTR
  useEffect(() => {
    if (hasTrackedImpression.current) return;
    if (SUPPRESS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) return;
    hasTrackedImpression.current = true;

    try {
      const sessionId = localStorage.getItem("vgp_session_id") || crypto.randomUUID();
      localStorage.setItem("vgp_session_id", sessionId);
      supabase
        .from("gateway_events")
        .insert({
          session_id: sessionId,
          event_type: "cta_band_view",
          source: new URLSearchParams(window.location.search).get("utm_source") || "organic",
          landing_page: pathname,
          path_chosen: `cta_variant_${variantId}`,
        })
        .then(({ error }) => {
          if (error) console.warn("[FinalCtaBand] impression tracking failed:", error.message);
        });
    } catch {
      /* tracking is best-effort */
    }
  }, [pathname, variantId]);

  // Suppress on non-marketing surfaces
  if (SUPPRESS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return null;
  }

  const onPricing = pathname === "/pricing" || pathname.startsWith("/pricing/");
  const secondaryHref = onPricing ? "/book-call" : "/pricing";
  const secondaryLabel = onPricing ? "Book a Strategy Call" : "See Pricing";

  return (
    <section
      aria-labelledby="final-cta-heading"
      data-cta-variant={variantId}
      className="relative overflow-hidden border-t border-secondary/15"
      style={{ background: "var(--gradient-hero, linear-gradient(135deg, hsl(220 30% 12%), hsl(220 30% 9%)))" }}
    >
      {/* Ambient gold glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute w-[480px] h-[480px] rounded-full bg-secondary/8 blur-[120px] -top-32 -right-24" />
        <div className="absolute w-96 h-96 rounded-full bg-secondary/6 blur-[100px] -bottom-24 left-10" />
      </div>

      <div className="container mx-auto px-6 py-14 md:py-20 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-secondary mb-4">
            {variant.eyebrow}
          </p>

          <h2
            id="final-cta-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.1] mb-5"
          >
            {variant.headline}
          </h2>

          <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto mb-9 leading-relaxed">
            {variant.subheadline}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8">
            <Button
              variant="premium"
              size="xl"
              asChild
              className="text-base sm:text-lg px-8 min-h-[56px]"
            >
              <Link to={variant.primaryHref} data-cta-variant={variantId}>
                <Calendar className="mr-2 w-5 h-5" />
                {variant.primaryLabel}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button
              variant="outline"
              size="xl"
              asChild
              className="text-base sm:text-lg px-8 min-h-[56px] border-white/25 text-white hover:bg-white/10 bg-transparent"
            >
              <Link to={secondaryHref}>{secondaryLabel}</Link>
            </Button>
          </div>

          {/* Mini trust strip */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/60">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-secondary" />
              Live in 14 days
            </span>
            <span className="hidden sm:inline w-px h-3 bg-white/15" />
            <span className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-secondary" />
              Month-to-month, no lock-in
            </span>
            <span className="hidden sm:inline w-px h-3 bg-white/15" />
            <span>
              <span className="text-secondary font-semibold">★ 4.9</span> · 40+ B2B operators
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCtaBand;
